import { LIVE_TREE, PUBLISHED_TREE, READY_TREE } from "library/constants.mjs"
import { Repeat } from "lib/@components/repeat"
import { Bound } from "lib/@components/binding/Bound"
import { Alert, Box, Grid, Stack } from "@mui/material"
import { FilterSchedule, RefreshScheduleSelector } from "event-definitions"
import { navigate } from "lib/routes/navigate"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { useEffect } from "react"
import { RegimeItem } from "routes/regime/regime-management/regime-item"
import { PlanRoot } from "library/tree/constants.mjs"
import { TreeRoot } from "library/tree/index.mjs"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { NoResults } from "lib/@components/no-results/no-results"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import DemandGuard from "minimals-template/components/guards/DemandGuard"
import { FEATURE_FLAG_REGIME_PERMISSIONS } from "routes/regime/plugins/regime-access/regime-access.runtime"
import { getDemandForFeature } from "lib/features"
import { RegimeSearch } from "routes/regime/plugins/regime-search/regime-search"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { getRegimeFacilitiesRoute } from "routes/regime/regime-list-routes"

export function RegimeList() {
    const ready = useTreeItem(READY_TREE)
    const live = useTreeItem(LIVE_TREE)
    const [share] = useSearchParam("share")

    const baseTree = {
        id: PUBLISHED_TREE,
        [PlanRoot]: true,
        [TreeRoot]: true,
        folderHandling: false,
        hasChildren: true,
        visible: false,
        children: [...(live?.children ?? []), ...(ready?.children ?? [])].unique((c) => c.id.split("|").at(-1)),
    }

    if (share && !live) {
        return (
            <DemandGuard
                demands={[
                    "publishRegime",
                    "manageRegime",
                    getDemandForFeature(FEATURE_FLAG_REGIME_PERMISSIONS),
                    "$sharing",
                ]}
            >
                <LoadingScreen description="Share Link Regimes" />
            </DemandGuard>
        )
    }

    return (
        <DemandGuard
            demands={[
                "publishRegime",
                "manageRegime",
                getDemandForFeature(FEATURE_FLAG_REGIME_PERMISSIONS),
                "$sharing",
            ]}
        >
            {!!live && (
                <Bound liveTrees={baseTree}>
                    <ActiveRegimes />
                </Bound>
            )}
        </DemandGuard>
    )
}

function ActiveRegimes() {
    const { liveTrees } = useBoundContext()
    const [share] = useSearchParam("share")
    const shouldAutoRedirect = liveTrees?.children?.length === 1
    RefreshScheduleSelector.useRefresh()
    useEffect(handleAutoRedirect, [shouldAutoRedirect, liveTrees, share])

    if (shouldAutoRedirect) {
        if (share) {
            return <LoadingScreen description="Share Link Redirect" />
        }
        return null
    }

    return (
        <PageBreadcrumbContainer title="Maintenance Regimes">
            <Box sx={{ pt: 3, flex: 1, overflowX: "hidden", overflowY: "scroll" }}>
                <Stack spacing={2}>
                    <RegimeSearch regimes={liveTrees.children} sx={{ width: 1 }} />
                    <Grid container spacing={3} sx={{ pr: 5 }}>
                        <Repeat
                            fallback={
                                share ? (
                                    <Alert severity="error" data-cy="invalid-share-link-alert">
                                        There are no longer any valid Maintenance Regimes available using this link.
                                        Please contact the person who supplied you with the link.
                                    </Alert>
                                ) : (
                                    <Box flex={1}>
                                        <NoResults />
                                    </Box>
                                )
                            }
                            bind
                            list={FilterSchedule.call({ items: liveTrees.children })?.items}
                            // The Facilities page lists regimes to pick from; the schedule count belongs on the
                            // Maintenance Regimes list and the regime heading, not here.
                            item={<RegimeItem hideMenu hideSummary go={go} />}
                        />
                    </Grid>
                </Stack>
            </Box>
        </PageBreadcrumbContainer>
    )

    function go(item) {
        navigate(getRegimeFacilitiesRoute(item.id, share))
    }

    function handleAutoRedirect() {
        if (!shouldAutoRedirect) return
        navigate(getRegimeFacilitiesRoute(liveTrees.children[0].id, share), { replace: true })
    }
}
